import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useSections, isSectionOn } from "@/hooks/useSections";
import * as Icons from "lucide-react";
import { ArrowRight, ChevronRight, Sparkles } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1758448656987-cfae6bf225e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjb3Jwb3JhdGUlMjBvZmZpY2V8ZW58MHx8fHwxNzg0NTc3NTU4fDA&ixlib=rb-4.1.0&q=85";

function ServiceIcon({ name }) {
  const map = {
    "shield-check": Icons.ShieldCheck,
    "gavel": Icons.Gavel,
    "activity": Icons.Activity,
    "graduation-cap": Icons.GraduationCap,
  };
  const Cmp = map[name] || Icons.Sparkles;
  return <Cmp size={26} className="text-gold" />;
}

export default function Home() {
  const { t, lang, pick } = useI18n();
  const sections = useSections();
  const [services, setServices] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [articles, setArticles] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [stats, setStats] = useState({});

  useEffect(() => {
    Promise.all([
      api.get("/services").then((r) => setServices(r.data)),
      api.get("/campaigns").then((r) => setCampaigns(r.data.slice(0, 2))),
      api.get("/articles").then((r) => setArticles(r.data.slice(0, 3))),
      api.get("/testimonials").then((r) => setTestimonials(r.data.slice(0, 3))),
      api.get("/homepage-stats").then((r) => setStats(r.data || {})),
    ]).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-navy text-cream min-h-[92vh] flex items-center">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(10,37,64,0.55) 0%, rgba(10,37,64,0.92) 100%)" }}
        />
        <div className="absolute inset-0 noise-overlay" />
        <div className="container-x relative py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 animate-fade-up">
            <span className="eyebrow inline-flex items-center gap-2"><Sparkles size={18} /> {t("hero.tagline")}</span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight text-cream">
              {t("hero.title")}
            </h1>
            <span className="divider-gold" />
            <p className="text-lg md:text-xl text-cream/85 max-w-2xl leading-relaxed">
              {t("hero.subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              {isSectionOn(sections, "assessment") && (
                <Link to="/assessment" data-testid="hero-cta-primary" className="btn-gold">
                  {t("hero.cta1")} <ArrowRight size={16} />
                </Link>
              )}
              <Link to="/services" data-testid="hero-cta-secondary" className="btn-outline-gold">
                {t("hero.cta2")}
              </Link>
            </div>
          </div>
          <div className="lg:col-span-4 flex flex-col justify-end gap-4 animate-fade-up">
            {Array.isArray(stats.hero_metrics) && stats.hero_metrics.length > 0 && (
              <div className="text-xs uppercase tracking-[0.22em] text-cream/60 mb-1" data-testid="hero-activity-title">
                {t("hero.activityTitle")}
              </div>
            )}
            {(Array.isArray(stats.hero_metrics) ? stats.hero_metrics : []).map((m) => (
              <div key={m.key} className="border-s-2 border-gold ps-5 py-2" data-testid={`hero-metric-${m.key}`}>
                <div className="text-3xl md:text-4xl font-serif text-gold leading-none">
                  {Number(m.value ?? 0).toLocaleString(lang === "ar" ? "ar-EG" : "en-GB")}
                </div>
                <div className="mt-2 text-xs uppercase tracking-[0.18em] text-cream/85 font-medium">
                  {lang === "ar" ? m.name_ar : m.name_en}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES — hidden from Homepage per request; still accessible via About and admin */}
      {false && (
      <section className="container-x py-24" data-testid="values-section">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div className="md:col-span-1">
            <span className="eyebrow">{t("home.valuesTitle")}</span>
            <h2 className="mt-3 text-3xl md:text-5xl leading-tight text-navy">
              {t("home.valuesSubtitle")}
            </h2>
            <span className="divider-gold" />
          </div>
          <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { t: lang === "ar" ? "خبرة إقليمية" : "Regional Depth", d: lang === "ar" ? "فهم عميق لبيئة الأعمال في الشرق الأوسط." : "Deep grounding in Middle East business realities." },
              { t: lang === "ar" ? "معايير عالمية" : "Global Standards", d: lang === "ar" ? "التزام بمعايير OECD وISO." : "Aligned with OECD and ISO standards." },
              { t: lang === "ar" ? "تنفيذ عملي" : "Pragmatic Execution", d: lang === "ar" ? "من الاستشارة إلى التطبيق الفعلي." : "From strategy to hands-on implementation." },
              { t: lang === "ar" ? "شفافية كاملة" : "Full Transparency", d: lang === "ar" ? "سجل حوكمة موثّق لكل تفاعل." : "Auditable log of every engagement touchpoint." },
            ].map((v, i) => (
              <div key={i} className="card-elev p-6 hover:-translate-y-1 transition-transform">
                <div className="w-8 h-8 rounded-sm bg-gold/15 flex items-center justify-center text-gold font-serif text-lg">{i + 1}</div>
                <h3 className="mt-4 text-lg text-navy">{v.t}</h3>
                <p className="mt-2 text-sm text-navy/70 leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* SERVICES */}
      <section className="bg-white py-24" data-testid="services-preview">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <span className="eyebrow">{t("home.servicesTitle")}</span>
              <h2 className="mt-3 text-3xl md:text-5xl text-navy">{t("home.servicesSubtitle")}</h2>
              <span className="divider-gold" />
            </div>
            <Link to="/services" className="text-navy hover:text-gold text-sm inline-flex items-center gap-2">
              {t("common.readMore")} <ChevronRight size={14} />
            </Link>
          </div>
          {/* Detailed service cards hidden from Homepage per request — full grid available at /services */}
          {false && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s) => (
              <div key={s.id || s.key} data-testid={`service-${s.key}`} className="border border-navy/10 bg-cream p-8 hover:border-gold/50 hover:-translate-y-1 transition-transform group">
                <ServiceIcon name={s.icon} />
                <h3 className="mt-6 text-xl text-navy">{pick(s, "title")}</h3>
                <p className="mt-3 text-sm text-navy/70 leading-relaxed">{pick(s, "desc")}</p>
                <div className="mt-6 text-xs uppercase tracking-widest text-gold group-hover:translate-x-1 inline-flex items-center gap-2 transition-transform">
                  {t("common.readMore")} <ArrowRight size={14} />
                </div>
              </div>
            ))}
          </div>
          )}
          <div className="mt-2">
            <Link to="/services" data-testid="services-cta" className="btn-gold">
              {t("hero.cta2")} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* CAMPAIGNS — detailed cards hidden from Homepage per request; full listing at /campaigns */}
      {false && isSectionOn(sections, "campaigns") && campaigns.length > 0 && (
        <section className="container-x py-24" data-testid="campaigns-preview">
          <div className="mb-12">
            <span className="eyebrow">{t("home.campaignsTitle")}</span>
            <h2 className="mt-3 text-3xl md:text-5xl text-navy">{t("home.campaignsSubtitle")}</h2>
            <span className="divider-gold" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {campaigns.map((c) => {
              const remaining = (c.total_slots || 0) - (c.filled_slots || 0);
              return (
                <div key={c.id} className="card-elev p-8 relative overflow-hidden">
                  <div className="absolute top-6 end-6">
                    <span className="chip !bg-navy !text-gold">
                      <span className="w-2 h-2 rounded-full bg-gold animate-pulse-slow" /> {remaining} {t("common.slotsLeft")}
                    </span>
                  </div>
                  <h3 className="text-2xl text-navy pe-32">{pick(c, "title")}</h3>
                  <p className="mt-3 text-sm text-navy/70 leading-relaxed">{pick(c, "desc")}</p>
                  <div className="mt-6 h-1.5 bg-navy/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gold"
                      style={{ width: `${Math.round(((c.filled_slots || 0) / (c.total_slots || 1)) * 100)}%` }}
                    />
                  </div>
                  <div className="mt-6 flex items-center justify-between">
                    <div className="text-xs text-navy/60">
                      {c.filled_slots}/{c.total_slots} {lang === "ar" ? "مقعد" : "seats"}
                    </div>
                    <Link to="/campaigns" className="btn-outline-navy !py-2 !px-4 text-xs">
                      {t("common.apply")}
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ARTICLES */}
      {isSectionOn(sections, "articles") && (
      <section className="bg-white py-24" data-testid="articles-preview">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
            <div>
              <span className="eyebrow">{t("home.articlesTitle")}</span>
              <h2 className="mt-3 text-3xl md:text-5xl text-navy">{lang === "ar" ? "رؤى وتحليلات" : "Perspectives & Analysis"}</h2>
              <span className="divider-gold" />
            </div>
            <Link to="/articles" className="text-navy hover:text-gold text-sm inline-flex items-center gap-2">
              {t("common.readMore")} <ChevronRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {articles.map((a) => (
              <article key={a.id} data-testid={`article-card-${a.id}`} className="border border-navy/10 bg-cream group">
                {a.cover_image && (
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={a.cover_image}
                      alt={pick(a, "title")}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                )}
                <div className="p-6">
                  <div className="chip !text-navy !border-navy/20 !bg-white">{pick(a, "tag")}</div>
                  <h3 className="mt-4 text-xl text-navy leading-snug">{pick(a, "title")}</h3>
                  <p className="mt-2 text-sm text-navy/70 line-clamp-3">{pick(a, "excerpt")}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* TESTIMONIALS */}
      {isSectionOn(sections, "testimonials") && testimonials.length > 0 && (
        <section className="container-x py-24" data-testid="testimonials-preview">
          <div className="mb-12 text-center max-w-2xl mx-auto">
            <span className="eyebrow">{t("home.testimonialsTitle")}</span>
            <h2 className="mt-3 text-3xl md:text-5xl text-navy">
              {lang === "ar" ? "ثقة قادة الأعمال" : "Trusted by Business Leaders"}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((tm) => {
              // If the admin uploaded a client letter / certificate image, make
              // it the primary visual and de-emphasise the quotation text.
              const hasCertificate = !!tm.image_url;
              return (
                <blockquote key={tm.id} className="card-elev overflow-hidden"
                            data-testid={`home-testimonial-${tm.id}`}>
                  {hasCertificate ? (
                    <a href={tm.image_url} target="_blank" rel="noopener noreferrer"
                       className="block bg-cream/60 border-b border-navy/10">
                      <img
                        src={tm.image_url} alt={pick(tm, "author")}
                        className="w-full max-h-[420px] object-contain bg-white"
                        data-testid={`home-testimonial-cert-${tm.id}`}
                      />
                    </a>
                  ) : (
                    <div className="p-8 pb-0">
                      <div className="text-gold text-3xl font-serif">“</div>
                      <p className="mt-2 text-navy/85 leading-relaxed">{pick(tm, "quote")}</p>
                    </div>
                  )}
                  <div className="p-6">
                    {hasCertificate && pick(tm, "quote") && (
                      <p className="text-navy/55 text-xs leading-relaxed italic line-clamp-2 mb-3">
                        “{pick(tm, "quote")}”
                      </p>
                    )}
                    <div className="pt-3 border-t border-navy/10 flex items-center gap-3">
                      <div>
                        <div className="font-medium text-navy">{pick(tm, "author")}</div>
                        <div className="text-[10px] text-navy/60 uppercase tracking-widest">
                          {[pick(tm, "role"), pick(tm, "company")].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                    </div>
                  </div>
                </blockquote>
              );
            })}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-navy text-cream py-20" data-testid="home-cta">
        <div className="container-x grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
          <div className="md:col-span-2">
            <span className="eyebrow">{t("home.ctaTitle")}</span>
            <h3 className={`mt-3 text-3xl md:text-4xl ${lang === "ar" ? "font-arabic-serif" : "font-serif"}`}>
              {t("home.ctaSub")}
            </h3>
          </div>
          <div className="flex md:justify-end gap-3">
            {isSectionOn(sections, "quote") && (
              <Link to="/quote" data-testid="footer-cta-quote" className="btn-gold">{t("nav.quote")}</Link>
            )}
            {isSectionOn(sections, "assessment") && (
              <Link to="/assessment" data-testid="footer-cta-assess" className="btn-outline-gold">{t("nav.assessment")}</Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
